<script lang="ts">
  import { Link, navigate } from 'svelte-routing'
  import type { Snippet } from 'svelte'
  import { isAuthenticated, session, currentRole, clearSession } from '$lib/auth/session'
  import { logout } from '$lib/auth/login-flow'
  import ThemeToggle from '../../components/ThemeToggle.svelte'

  interface Props {
    children?: Snippet
  }
  const { children }: Props = $props()

  let loggingOut = $state(false)

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true })
  })

  async function handleLogout(): Promise<void> {
    if (loggingOut) return
    loggingOut = true
    try {
      await logout()
      clearSession()
      navigate('/login', { replace: true })
    } finally {
      loggingOut = false
    }
  }
</script>

<div class="admin-shell">
  <aside class="sidebar">
    <div class="brand">RentReadyPet</div>
    <nav>
      <Link to="/admin">Dashboard</Link>
      <Link to="/admin/people">People</Link>
      <Link to="/admin/animals">Animals</Link>
      <Link to="/admin/occupancies">Occupancies</Link>
      <Link to="/admin/property-managers">Property Managers</Link>
      <Link to="/admin/units">Units</Link>
      <Link to="/admin/assistance-requests">Assistance</Link>
      {#if $currentRole === 'MANAGER' || $currentRole === 'ADMIN'}
        <Link to="/admin/revenue-audit">Revenue audit</Link>
        <Link to="/admin/leads">Leads</Link>
      {/if}
      {#if $currentRole === 'ADMIN'}
        <Link to="/admin/users">Users</Link>
      {/if}
    </nav>
    <div class="footer">
      <div class="footer-row">
        {#if $session.user}
          <div class="user">{$session.user.email}</div>
        {/if}
        <ThemeToggle />
      </div>
      <button type="button" onclick={handleLogout} disabled={loggingOut}>
        {loggingOut ? 'Logging out…' : 'Log out'}
      </button>
    </div>
  </aside>
  <main class="content">
    {#if children}
      {@render children()}
    {/if}
  </main>
</div>

<style>
  .admin-shell {
    display: grid;
    grid-template-columns: 220px 1fr;
    min-height: 100vh;
  }
  .sidebar {
    /* The admin sidebar is fixed dark chrome in both themes (not surface tokens,
       which would flip light in dark and drop white nav text below AA). */
    background: #1f2937;
    color: white;
    padding: 16px;
    display: flex;
    flex-direction: column;
  }
  .brand {
    font-weight: 600;
    font-size: 1.1rem;
    margin-bottom: 24px;
  }
  nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }
  nav :global(a) {
    color: #d1d5db;
    text-decoration: none;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.9rem;
  }
  nav :global(a:hover),
  nav :global(a[aria-current='page']) {
    background: var(--brand);
    color: white;
  }
  .footer {
    padding-top: 16px;
    border-top: 1px solid #374151;
  }
  .footer-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
  }
  /* The toggle sits on the fixed dark sidebar, so give it matching dark chrome
     in both themes instead of the default light-surface chip. */
  .footer-row :global(.theme-toggle) {
    background: transparent;
    color: #d1d5db;
    border-color: #374151;
  }
  .footer-row :global(.theme-toggle:hover) {
    border-color: #6b7280;
  }
  .user {
    font-size: 0.8rem;
    /* on the fixed dark sidebar, AA in both themes */
    color: #9ca3af;
  }
  .footer button {
    width: 100%;
    padding: 8px;
    /* A danger action reads red in both themes (white text passes AA on both),
       so these stay fixed rather than flipping with the surface tokens. */
    background: #b91c1c;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  .footer button:hover {
    background: #991b1b;
  }
  .content {
    padding: 32px;
    background: var(--surface);
    overflow-y: auto;
  }
</style>
